// B3 (docs/backlog.md, PRD P0.4). The Edit Bottle / Inventory item
// editor's Strength field has ONE stored value, `liquorProducts.abvBasisPoints`
// (db/schema.ts), for every item kind. US proof is exactly twice ABV, so a
// second stored column would be two sources of truth for one fact
// (the backlog row's own words); this module is presentation-boundary-only,
// the same shape money.ts already established for tax rate and price:
// storage stays one integer, and the human-facing unit is derived at the
// edge, never stored.
//
// Pure, no DOM (see test/ui-has-permission.test.ts's own header for why a
// plain node:test file can import a ui/src module directly): none of the
// functions below touch `window` or `document`.

import { basisPointsToPercentString, parseExactDecimalToSmallestUnit } from "./money.ts";

/** The unit an item kind's Strength field is entered and displayed in.
 * Liquor is conventionally described by proof in the US; every other
 * sellable item kind (draft beer, packaged, and wine, see this module's
 * own STRENGTH_ITEM_KIND_JUDGMENT_CALL note below) is conventionally
 * described by ABV percent. */
export type StrengthUnit = "proof" | "abv_percent";

/** Judgment call, flagged in docs/build-briefs/B3-strength-by-item-kind.md
 * rather than inferred silently: the backlog row names only "liquor"
 * (Proof) and "draft beer and packaged" (ABV %) and says nothing about
 * "wine". Wine's ABV is never conventionally expressed as a proof value,
 * and this form already groups wine with beer/packaged for every
 * other field (applyBottleItemKindVisibility in admin.ts), so wine is
 * treated as abv_percent here, matching that existing grouping and
 * ordinary practice. Not an owner ruling; named for confirmation. */
export function strengthUnitForItemKind(itemKind: string): StrengthUnit {
  return itemKind === "liquor" ? "proof" : "abv_percent";
}

/** The Strength field's label text for the given item kind. */
export function strengthFieldLabel(itemKind: string): string {
  return strengthUnitForItemKind(itemKind) === "proof" ? "Proof" : "ABV";
}

/** Whether the field's "%" suffix affix should be shown. Proof is a bare
 * number (e.g. "80"), never a percentage, so it carries no "%" affix. */
export function strengthFieldShowsPercentSuffix(itemKind: string): boolean {
  return strengthUnitForItemKind(itemKind) === "abv_percent";
}

/** Converts the STORED fact (ABV basis points, e.g. 4000 = 40.00% ABV)
 * into the display string the given item kind's unit shows. Liquor
 * doubles it (proof = ABV x 2, the backlog row's own words) before
 * formatting; every other kind formats the stored value unchanged.
 * basisPointsToPercentString is lossless (it only ever inserts a decimal
 * point, never rounds), and doubling an integer stays an integer, so this
 * conversion never loses precision. */
export function abvBasisPointsToStrengthDisplayString(
  abvBasisPoints: number,
  itemKind: string,
): string {
  const unit = strengthUnitForItemKind(itemKind);
  const displayBasisPoints = unit === "proof" ? abvBasisPoints * 2 : abvBasisPoints;
  return basisPointsToPercentString(displayBasisPoints);
}

/** Parses a Strength field's raw text, entered under the given item
 * kind's unit, back into ABV basis points for storage. Exact and
 * rejecting, matching this codebase's one parsing convention
 * (parseExactDecimalToSmallestUnit): a Proof value whose ABV equivalent
 * would need more precision than the stored column's 0.01% step (an odd
 * number of proof basis points) is refused by name rather than silently
 * truncated, the same "reject, never round" rule Amendment 2026-08-22
 * (second) established for tax rate and bottle price. */
export function parseStrengthDisplayStringToAbvBasisPoints(raw: string, itemKind: string): number {
  const unit = strengthUnitForItemKind(itemKind);
  if (unit === "abv_percent") {
    return parseExactDecimalToSmallestUnit(raw, 2, "ABV", "0.01%");
  }
  const proofBasisPoints = parseExactDecimalToSmallestUnit(raw, 2, "Proof", "0.01 proof");
  if (proofBasisPoints % 2 !== 0) {
    throw new RangeError(
      `Proof can't be more precise than its ABV equivalent supports (0.01%). "${raw.trim()}" ` +
        "would need a fraction of a hundredth of a percent ABV to store exactly.",
    );
  }
  return proofBasisPoints / 2;
}

/** THE RE-SCALE OPERATION (docs/backlog.md B3's own named bug risk):
 * re-expresses whatever is currently typed in the Strength field, when
 * the item kind changes, in the NEW kind's unit, WITHOUT reinterpreting
 * the same digits as already being in that new unit (e.g. never turns a
 * typed "40" proof into a stored "40" percent ABV). Blank input stays
 * blank. Input that does not parse under the OLD kind's unit (a field the
 * user is still mid-typing, or invalid text) is returned unchanged rather
 * than guessed at; save-time validation still catches it under whichever
 * unit is current at Save. */
export function rescaleStrengthDisplayString(
  raw: string,
  fromItemKind: string,
  toItemKind: string,
): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return raw;
  }
  let abvBasisPoints: number;
  try {
    abvBasisPoints = parseStrengthDisplayStringToAbvBasisPoints(trimmed, fromItemKind);
  } catch {
    return raw;
  }
  return abvBasisPointsToStrengthDisplayString(abvBasisPoints, toItemKind);
}
